import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import publicFormService from '../../services/publicFormService';
import '../../assets/styles/auth.css';
import './PublicFormFill.css';

/**
 * The public, unauthenticated side of a PublicForm (see backend
 * routes/public.js) - reached via a shared link, filled in by people
 * outside the organisation with no account and no login. Anonymity is
 * whatever the form itself asks (or doesn't ask) for; this page never
 * collects anything beyond the configured questions.
 */
const PublicFormFill = () => {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await publicFormService.getBySlug(slug);
        setForm(data.form);
      } catch (err) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const setAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const toggleCheckbox = (questionId, option) => {
    setAnswers((prev) => {
      const current = Array.isArray(prev[questionId]) ? prev[questionId] : [];
      const next = current.includes(option) ? current.filter((o) => o !== option) : [...current, option];
      return { ...prev, [questionId]: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const missing = (form.questions || []).find((q) => {
      const value = answers[q.id];
      return q.required && (value === undefined || value === '' || (Array.isArray(value) && value.length === 0));
    });
    if (missing) {
      setError(`Please answer "${missing.label}"`);
      return;
    }

    try {
      setSubmitting(true);
      await publicFormService.submit(slug, answers);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong submitting this form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (question) => {
    switch (question.type) {
      case 'textarea':
        return (
          <textarea
            rows="4"
            className="form-control"
            value={answers[question.id] || ''}
            onChange={(e) => setAnswer(question.id, e.target.value)}
            required={question.required}
          />
        );
      case 'select':
        return (
          <select
            className="form-control"
            value={answers[question.id] || ''}
            onChange={(e) => setAnswer(question.id, e.target.value)}
            required={question.required}
          >
            <option value="">Select...</option>
            {question.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        );
      case 'radio':
        return (
          <div className="form-options">
            {question.options.map((opt) => (
              <label
                key={opt}
                className={`form-option ${answers[question.id] === opt ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  name={question.id}
                  value={opt}
                  checked={answers[question.id] === opt}
                  onChange={() => setAnswer(question.id, opt)}
                />
                <div className="form-option-content">
                  <h4>{opt}</h4>
                </div>
              </label>
            ))}
          </div>
        );
      case 'checkbox':
        return (
          <div className="form-options">
            {question.options.map((opt) => (
              <label
                key={opt}
                className={`form-option ${(answers[question.id] || []).includes(opt) ? 'selected' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={(answers[question.id] || []).includes(opt)}
                  onChange={() => toggleCheckbox(question.id, opt)}
                />
                <div className="form-option-content">
                  <h4>{opt}</h4>
                </div>
              </label>
            ))}
          </div>
        );
      case 'date':
        return (
          <input
            type="date"
            className="form-control"
            value={answers[question.id] || ''}
            onChange={(e) => setAnswer(question.id, e.target.value)}
            required={question.required}
          />
        );
      default:
        return (
          <input
            type="text"
            className="form-control"
            value={answers[question.id] || ''}
            onChange={(e) => setAnswer(question.id, e.target.value)}
            required={question.required}
          />
        );
    }
  };

  return (
    <div className="auth-page public-form-page">
      <div className="auth-container public-form-container">
        <div className="auth-form-section">
          <div className="auth-logo">
            <i className="fas fa-shield-alt"></i>
            <span>SafeWork</span>
          </div>

          {loading ? (
            <div className="auth-header">
              <h1>Loading...</h1>
            </div>
          ) : notFound ? (
            <div className="auth-header">
              <h1>Form not available</h1>
              <p>This link may have expired or is incorrect. Please check with whoever shared it with you.</p>
            </div>
          ) : submitted ? (
            <>
              <div className="auth-alert success">
                <i className="fas fa-check-circle"></i>
                <div>
                  <strong>Thank you</strong>
                  <p style={{ margin: '0.25rem 0 0' }}>Your submission has been received.</p>
                </div>
              </div>
              <div className="auth-header">
                <h1>All done</h1>
                <p>You can close this page now.</p>
              </div>
            </>
          ) : (
            <>
              <div className="auth-header">
                <h1>{form.title}</h1>
                {form.description && <p>{form.description}</p>}
              </div>

              {error && (
                <div className="auth-alert error">
                  <i className="fas fa-exclamation-circle"></i>
                  <span>{error}</span>
                </div>
              )}

              <form className="auth-form" onSubmit={handleSubmit}>
                {(form.questions || []).map((question) => (
                  <div key={question.id} className="form-group">
                    <label>{question.label}{question.required ? ' *' : ''}</label>
                    {renderQuestion(question)}
                  </div>
                ))}

                <button type="submit" className="btn-auth-primary" disabled={submitting}>
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm"></span>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane"></i>
                      Submit
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicFormFill;
